/**
 * @file NotificationTemplateContentPreview.js
 * @description Subject field plus a Message Preview card with Email/SMS/Push channel tabs, matching the
 * notification-template spec layout. Also hosts the "view definitions" drawer and "view history" popover.
 */
import { useEffect, useState, useRef } from 'react';
import styles from './NotificationTemplateContentPreview.module.scss';
import { CharacterLimitIndicator } from '../../helpers/CharacterLimitIndicator';
import { useWindowListener } from '../../helpers/useWindowListener';
import SideDrawer from '../../helpers/SideDrawer';
import NotificationTemplateEmailTab from './NotificationTemplateEmailTab';
import NotificationTemplateSmsTab from './NotificationTemplateSmsTab';
import NotificationTemplatePushTab from './NotificationTemplatePushTab';
import { formatDateToMmmDDYYYY } from '../../helpers/DateUtils';
import { getText } from '../../../i18n';
import {
    sanitizeNotificationChannelText,
    shouldAutoSyncChannel,
    htmlToPlainText,
} from '../../../utils/htmlText';

const CHANNELS = {
    EMAIL: 'email',
    SMS: 'sms',
    PUSH: 'push',
};

const CHANNEL_TABS = [
    {
        value: CHANNELS.EMAIL,
        label: getText('msg_84add5b29527'),
        icon: 'bi-envelope',
    },
    {
        value: CHANNELS.SMS,
        label: getText('msg_9e10d4517d5a'),
        icon: 'bi-chat-text',
    },
    {
        value: CHANNELS.PUSH,
        label: getText('msg_8f7f57b519fc'),
        icon: 'bi-bell',
    },
];

const DEFINITIONS_SECTIONS = [
    {
        label: getText('msg_971d6ded4edf'),
        text: getText('msg_88c218cfb2d1'),
    },
    {
        label: getText('msg_cf73be80fa6a'),
        text: getText('msg_f215107d6a76'),
    },
    {
        label: getText('msg_0b475609baf3'),
        text: getText('msg_0011a3b60a68'),
    },
    {
        label: getText('msg_d42713493ca8'),
        text: getText('msg_36d9a16d546a'),
    },
];

const SUBJECT_MAX_CHARS = 50;

const NotificationTemplateContentPreview = ({
    templateData,
    isCreating,
    isReadOnly,
    showHistory = true,
    onSubjectChange,
    onHtmlBodyChange,
    onPlainTextBodyChange,
    onPushBodyChange,
    onDestinationChange,
}) => {
    const [activeChannel, setActiveChannel] = useState(CHANNELS.EMAIL);
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [isDefinitionsOpen, setIsDefinitionsOpen] = useState(false);
    const historyRef = useRef(null);

    const [isPlainTextCustom, setIsPlainTextCustom] = useState(
        !!htmlToPlainText(templateData.plainTextBody || '').trim(),
    );
    const [isPushTextCustom, setIsPushTextCustom] = useState(
        !!htmlToPlainText(templateData.pushBody || '').trim(),
    );
    const [isSmsAutoLinked, setIsSmsAutoLinked] = useState(
        !htmlToPlainText(templateData.plainTextBody || '').trim(),
    );
    const [isPushAutoLinked, setIsPushAutoLinked] = useState(
        !htmlToPlainText(templateData.pushBody || '').trim(),
    );

    // Treat RichTextEditor initialContent as a one-time seed, not a controlled value.
    // Re-seed only when a different template is loaded.
    const [emailEditorSeed, setEmailEditorSeed] = useState(
        templateData.htmlBody || '',
    );
    const [smsEditorSeed, setSmsEditorSeed] = useState(
        templateData.plainTextBody || '',
    );
    const [pushEditorSeed, setPushEditorSeed] = useState(
        templateData.pushBody || '',
    );

    const [editorResetKeys, setEditorResetKeys] = useState({
        email: 0,
        sms: 0,
        push: 0,
    });

    useEffect(() => {
        setEmailEditorSeed(templateData.htmlBody || '');
        setSmsEditorSeed(templateData.plainTextBody || '');
        setPushEditorSeed(templateData.pushBody || '');
        setIsPlainTextCustom(
            !!htmlToPlainText(templateData.plainTextBody || '').trim(),
        );
        setIsPushTextCustom(
            !!htmlToPlainText(templateData.pushBody || '').trim(),
        );
        setIsSmsAutoLinked(
            !htmlToPlainText(templateData.plainTextBody || '').trim(),
        );
        setIsPushAutoLinked(
            !htmlToPlainText(templateData.pushBody || '').trim(),
        );

        setEditorResetKeys(prev => ({
            email: prev.email + 1,
            sms: prev.sms + 1,
            push: prev.push + 1,
        }));
    }, [templateData.id]);

    const handleEmailBodyChange = value => {
        const autoSummary = sanitizeNotificationChannelText(value, 'sms');
        const autoPushSummary = sanitizeNotificationChannelText(value, 'push');

        onHtmlBodyChange(value);

        if (
            shouldAutoSyncChannel({
                isChannelCustom: isPlainTextCustom,
                isAutoLinked: isSmsAutoLinked,
            })
        ) {
            setSmsEditorSeed(autoSummary);
            setIsPlainTextCustom(false);
            setIsSmsAutoLinked(true);
            onPlainTextBodyChange(autoSummary);
            setEditorResetKeys(prev => ({ ...prev, sms: prev.sms + 1 }));
        }

        if (
            shouldAutoSyncChannel({
                isChannelCustom: isPushTextCustom,
                isAutoLinked: isPushAutoLinked,
            })
        ) {
            setPushEditorSeed(autoPushSummary);
            setIsPushTextCustom(false);
            setIsPushAutoLinked(true);
            onPushBodyChange(autoPushSummary);
            setEditorResetKeys(prev => ({ ...prev, push: prev.push + 1 }));
        }
    };

    const handleSmsBodyChange = value => {
        setIsPlainTextCustom(true);
        setIsSmsAutoLinked(false);
        onPlainTextBodyChange(value);
    };

    const handlePushBodyChange = value => {
        setIsPushTextCustom(true);
        setIsPushAutoLinked(false);
        onPushBodyChange(value);
    };

    useWindowListener('mousedown', event => {
        if (historyRef.current && !historyRef.current.contains(event.target)) {
            setIsHistoryOpen(false);
        }
    });

    const lastEditedAt = templateData.updatedAt || templateData.createdAt;
    const editedBy = templateData.updatedBy || templateData.createdBy;

    return (
        <div className={styles['notification-template-content-shell']}>
            <div className={styles['notification-template-subject-section']}>
                <div className="file-form-label">
                    {getText('msg_8d183dbdcea3')}
                    <span
                        style={{
                            color: 'var(--iNGAGED-Destructive-Color)',
                            marginLeft: '4px',
                        }}
                    >
                        *
                    </span>
                </div>
                <input
                    value={templateData.subject || ''}
                    onChange={event => onSubjectChange(event.target.value)}
                    className="form-control"
                    type="text"
                    placeholder={getText('msg_0a359f97edcb')}
                    aria-label={getText('msg_df9820b57cc2')}
                    disabled={isReadOnly}
                    style={{
                        height: '48px',
                        borderRadius: '12px',
                        border: 'solid 1px rgba(47, 43, 67, 0.2)',
                        paddingLeft: '16px',
                        paddingRight: '16px',
                    }}
                />
                <CharacterLimitIndicator
                    maxChars={SUBJECT_MAX_CHARS}
                    inputStr={templateData.subject || ''}
                />
            </div>

            <div className={styles['notification-template-preview-header']}>
                <div className={styles.previewRowLeft}>
                    <span className={styles.previewLabel}>
                        {getText('msg_584654ddda43')}
                    </span>
                    <button
                        type="button"
                        className={styles.previewLink}
                        onClick={() => setIsDefinitionsOpen(true)}
                    >
                        {getText('msg_83db64acf280')}
                    </button>
                </div>
                {showHistory && !isCreating && (
                    <span className={styles.historyAnchor} ref={historyRef}>
                        <button
                            type="button"
                            className={styles.previewLink}
                            onClick={() => setIsHistoryOpen(prev => !prev)}
                        >
                            {getText('msg_9c0d1e2f3a4b')}
                        </button>
                        {isHistoryOpen && (
                            <div className={styles.historyPopover}>
                                <div className={styles.historyTitle}>
                                    {getText('msg_90ccd6497400')}
                                </div>
                                <div className="d-flex" style={{ gap: '24px' }}>
                                    <div>
                                        <div className={styles.historyLabel}>
                                            {getText('msg_0c79adcf41ec')}
                                        </div>
                                        <div className={styles.historyValue}>
                                            {lastEditedAt
                                                ? formatDateToMmmDDYYYY(
                                                      lastEditedAt,
                                                  )
                                                : '—'}
                                        </div>
                                    </div>
                                    <div>
                                        <div className={styles.historyLabel}>
                                            {getText('msg_6180d5db3ed9')}
                                        </div>
                                        <div className={styles.historyValue}>
                                            {editedBy || '—'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </span>
                )}
            </div>

            <div className="card">
                <div className="card-body">
                    <div className={styles.channelTabs}>
                        {CHANNEL_TABS.map(tab => {
                            const isActive = activeChannel === tab.value;
                            return (
                                <button
                                    key={tab.value}
                                    type="button"
                                    className={`${styles.channelTabButton} ${isActive ? styles.channelTabActive : ''}`}
                                    onClick={() => setActiveChannel(tab.value)}
                                >
                                    {tab.label}
                                    <i className={`bi ${tab.icon}`} />
                                </button>
                            );
                        })}
                    </div>

                    <div className={styles.channelBlock}>
                        <div
                            style={{
                                display:
                                    activeChannel === CHANNELS.EMAIL
                                        ? 'block'
                                        : 'none',
                            }}
                        >
                            <NotificationTemplateEmailTab
                                initialHtmlBody={emailEditorSeed}
                                onHtmlBodyChange={handleEmailBodyChange}
                                isReadOnly={isReadOnly}
                                agencyId={templateData.agencyId}
                            />
                        </div>

                        <div
                            style={{
                                display:
                                    activeChannel === CHANNELS.SMS
                                        ? 'block'
                                        : 'none',
                            }}
                        >
                            <NotificationTemplateSmsTab
                                key={`template-sms-${editorResetKeys.sms}`}
                                initialPlainTextBody={smsEditorSeed}
                                currentPlainTextBody={
                                    templateData.plainTextBody
                                }
                                onPlainTextBodyChange={handleSmsBodyChange}
                                isReadOnly={isReadOnly}
                                agencyId={templateData.agencyId}
                            />
                        </div>

                        <div
                            style={{
                                display:
                                    activeChannel === CHANNELS.PUSH
                                        ? 'block'
                                        : 'none',
                            }}
                        >
                            <NotificationTemplatePushTab
                                key={`template-push-${editorResetKeys.push}`}
                                initialPushBody={pushEditorSeed}
                                currentPushBody={templateData.pushBody}
                                pushDestinationTypeId={
                                    templateData.pushDestinationTypeId
                                }
                                pushDestinationValue={
                                    templateData.pushDestinationValue
                                }
                                onPushBodyChange={handlePushBodyChange}
                                onDestinationChange={onDestinationChange}
                                isReadOnly={isReadOnly}
                                agencyId={templateData.agencyId}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <SideDrawer
                isOpen={isDefinitionsOpen}
                setIsOpen={setIsDefinitionsOpen}
                header={getText('msg_c60bb0cab7d8')}
                sections={DEFINITIONS_SECTIONS}
            />
        </div>
    );
};

export default NotificationTemplateContentPreview;
